import { UserService } from './user.service';

import { inject, Injectable } from '@angular/core';

import { verifyPhoneNumber } from '../constants/constants';

export type DeliveryTargetType = 'email' | 'sms' | 'fax';

export interface DeliveryTargets {
  email: string[];
  sms: string[];
  fax: string[];
}

export interface ProductAddressPosition {
  productIndex: number;
  addressIndex: number;
}

export interface FormControlAddressPosition {
  formControlName: string;
  position: number;
}

@Injectable({
  providedIn: 'root',
})
export class UserAdminDeliveryTargetService {
  private readonly userService = inject(UserService);

  readonly emptyDeliveryTargets: DeliveryTargets = {
    email: [],
    sms: [],
    fax: [],
  };

  createInitialDeliveryTargets(email?: string): DeliveryTargets {
    return {
      email: email ? [email] : [],
      sms: [],
      fax: [],
    };
  }

  normalizeTargetValue(
    targetType: DeliveryTargetType,
    value: string
  ): string | null {
    const trimmedValue = value.trim();

    if (!trimmedValue) {
      return null;
    }

    if (targetType === 'email') {
      return trimmedValue;
    }

    return verifyPhoneNumber(trimmedValue);
  }

  shouldAddTarget(
    deliveryTargets: DeliveryTargets,
    targetType: DeliveryTargetType,
    value: string
  ): boolean {
    return !!value && !deliveryTargets[targetType].includes(value);
  }

  addTarget(
    deliveryTargets: DeliveryTargets,
    targetType: DeliveryTargetType,
    value: string
  ): DeliveryTargets {
    return {
      ...deliveryTargets,
      [targetType]: [...deliveryTargets[targetType], value],
    };
  }

  removeTarget(
    deliveryTargets: DeliveryTargets,
    targetType: DeliveryTargetType,
    index: number
  ): DeliveryTargets {
    return {
      ...deliveryTargets,
      [targetType]: deliveryTargets[targetType].filter((_, i) => i !== index),
    };
  }

  getProductAddressPositions(
    userData: any,
    targetType: DeliveryTargetType,
    address: string
  ): ProductAddressPosition[] {
    const productPositions: ProductAddressPosition[] = [];
    const deliveryProducts = userData?.metadata?.delivery?.product;

    if (!Array.isArray(deliveryProducts)) {
      return productPositions;
    }

    deliveryProducts.forEach((product, productIndex) => {
      const addresses = product?.[targetType];

      if (!Array.isArray(addresses)) {
        return;
      }

      addresses.forEach((currentAddress, addressIndex) => {
        if (currentAddress === address) {
          productPositions.push({ productIndex, addressIndex });
        }
      });
    });

    return productPositions;
  }

  getFormControlAddressPositions(
    formGroupDelivery: any,
    targetType: DeliveryTargetType,
    address: string
  ): FormControlAddressPosition[] {
    const positions: FormControlAddressPosition[] = [];

    if (!formGroupDelivery?.controls) {
      return positions;
    }

    for (const formControlName in formGroupDelivery.controls) {
      if (!/^ADDRESSESx/.test(formControlName)) {
        continue;
      }

      const control = formGroupDelivery.controls[formControlName];

      if (!Array.isArray(control.value)) {
        continue;
      }

      control.value.forEach((addressValue: string, position: number) => {
        if (!addressValue.startsWith(targetType)) {
          return;
        }

        const extractedAddress = addressValue.substring(targetType.length);

        if (address === extractedAddress) {
          positions.push({ formControlName, position });
        }
      });
    }

    return positions;
  }

  removeTargetFromProducts(
    targetType: DeliveryTargetType,
    productPositions: ProductAddressPosition[]
  ): void {
    for (const position of productPositions) {
      this.userService.userData.update((userData: any) => {
        const updatedProduct = structuredClone(
          userData.metadata.delivery.product
        );

        updatedProduct[position.productIndex][targetType].splice(
          position.addressIndex,
          1
        );

        return {
          ...userData,
          metadata: {
            ...userData.metadata,
            delivery: {
              ...userData.metadata.delivery,
              product: updatedProduct,
            },
          },
        };
      });
    }
  }

  removeTargetFromFormControls(
    formControlPositions: FormControlAddressPosition[]
  ): void {
    for (const position of formControlPositions) {
      this.userService.formGroupDelivery.controls[
        position.formControlName
      ].value.splice(position.position, 1);
    }
  }

  createDeliveryTargetsFromUserData(
    userData: any,
    fallbackEmail = ''
  ): DeliveryTargets {
    let deliveryTargets = this.createInitialDeliveryTargets(fallbackEmail);

    deliveryTargets = this.addTargetsFromMetadataTargets(
      deliveryTargets,
      userData
    );

    deliveryTargets = this.addTargetsFromActiveDeliveryProducts(
      deliveryTargets,
      userData
    );

    return deliveryTargets;
  }

  private addTargetsFromMetadataTargets(
    deliveryTargets: DeliveryTargets,
    userData: any
  ): DeliveryTargets {
    const targets = userData?.metadata?.delivery?.target;

    if (!targets) {
      return deliveryTargets;
    }

    let updatedTargets = { ...deliveryTargets };

    for (const targetType of Object.keys(
      updatedTargets
    ) as DeliveryTargetType[]) {
      const values = targets[targetType];

      if (!Array.isArray(values)) {
        continue;
      }

      for (const value of values) {
        const normalizedValue = this.normalizeTargetValue(targetType, value);

        if (
          normalizedValue &&
          !updatedTargets[targetType].includes(normalizedValue)
        ) {
          updatedTargets = this.addTarget(
            updatedTargets,
            targetType,
            normalizedValue
          );
        }
      }
    }

    return updatedTargets;
  }

  private addTargetsFromActiveDeliveryProducts(
    deliveryTargets: DeliveryTargets,
    userData: any
  ): DeliveryTargets {
    const deliveryProducts = userData?.metadata?.delivery?.product;

    if (!Array.isArray(deliveryProducts)) {
      return deliveryTargets;
    }

    let updatedTargets = { ...deliveryTargets };

    for (const product of deliveryProducts) {
      if (product?.active !== true) {
        continue;
      }

      for (const targetType of Object.keys(
        updatedTargets
      ) as DeliveryTargetType[]) {
        const values = product[targetType];

        if (!Array.isArray(values)) {
          continue;
        }

        for (const value of values) {
          const normalizedValue = this.normalizeTargetValue(targetType, value);

          if (
            normalizedValue &&
            !updatedTargets[targetType].includes(normalizedValue)
          ) {
            updatedTargets = this.addTarget(
              updatedTargets,
              targetType,
              normalizedValue
            );
          }
        }
      }
    }

    return updatedTargets;
  }
}
